import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/authContext';
import { googleSignOut } from '../../services/firebase';
import { CustomerAtom } from '../../atoms/customerAtom';
import { useRecoilState } from 'recoil';
import { CustomerServiceContext } from '../../context/customer';
import { useTheme } from '../../context/ThemeContext';
import { 
  KeyIcon, 
  UserCircleIcon, 
  ArrowRightOnRectangleIcon, 
  ClipboardIcon, 
  ClipboardDocumentCheckIcon,
  ShieldCheckIcon,
  KeyIcon as KeyIconSolid,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { KeyIcon as KeyIconSolidFilled } from '@heroicons/react/24/solid';
import axios from 'axios';
// import { TickerDataDownload } from './TickerDataDownload';

export const Profile: React.FC = () => {
  const customerService = React.useContext(CustomerServiceContext);
  const [customer, setCustomer] = useRecoilState(CustomerAtom);
  const { user, userLogin, loading, handleSignOut } = useAuth();
  const { isDarkMode } = useTheme();
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [ticker, setTicker] = useState('');
  const [interval, setInterval] = useState('1d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);


  if (!userLogin) {
    window.location.href = '#/login';
  }

  useEffect(() => {
    if (user !== undefined && user?.email && customer === undefined) {
      console.log('user', user);
      customerService.getCustomerByEmail(user.email as string);
    }
  }, [user, customer]);

  const copyToClipboard = (text: string, type: 'key' | 'secret') => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'key') {
        setCopiedKey(true);
        setTimeout(() => setCopiedKey(false), 2000);
      } else {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      }
    });
  };

  const handleSignOutClick = async () => {
    try {
      await googleSignOut(setCustomer, handleSignOut);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  if (customer !== undefined) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          {/* Profile Header Card */}
          <div className={`mb-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
            <div className={`relative h-32 ${isDarkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`}>
              <div className="absolute -bottom-12 left-8">
                <div className="relative">
                  <img
                    alt=""
                    src={customer?.profile_image as string}
                    className="h-24 w-24 rounded-xl ring-4 ring-white shadow-lg"
                  />
                  <div className={`absolute bottom-0 right-0 h-6 w-6 rounded-full ${isDarkMode ? 'bg-green-500' : 'bg-green-400'} border-2 ${isDarkMode ? 'border-gray-800' : 'border-white'}`}></div>
                </div>
              </div>
            </div>
            <div className="pt-16 pb-8 px-8">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {customer?.username}
                  </h2>
                  <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {customer?.email}
                  </p>
                </div>
                <button
                  onClick={handleSignOutClick}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200 ${isDarkMode ? 'focus:ring-offset-gray-800' : ''}`}
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* API Credentials Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* API Key Card */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/10' : 'bg-indigo-50'}`}>
                    <KeyIconSolid className={`h-6 w-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      API Key
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Your unique API key for authentication
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} font-mono text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {customer?.api_key}
                </div>
                <button
                  onClick={() => copyToClipboard(customer?.api_key as string, 'key')}
                  className={`mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 ${isDarkMode ? 'focus:ring-offset-gray-800' : ''}`}
                >
                  {copiedKey ? (
                    <>
                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                      Copied to clipboard!
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="h-4 w-4 mr-2" />
                      Copy API Key
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* API Secret Card */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-lg overflow-hidden`}>
              <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                    <ShieldCheckIcon className={`h-6 w-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      API Secret
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Your secure API secret key
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} font-mono text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {customer?.api_secret}
                </div>
                <button
                  onClick={() => copyToClipboard(customer?.api_secret as string, 'secret')}
                  className={`mt-4 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200 ${isDarkMode ? 'focus:ring-offset-gray-800' : ''}`}
                >
                  {copiedSecret ? (
                    <>
                      <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                      Copied to clipboard!
                    </>
                  ) : (
                    <>
                      <ClipboardIcon className="h-4 w-4 mr-2" />
                      Copy API Secret
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Ticker Data Download Section */}
          {/* <TickerDataDownload customer={customer} /> */}
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className={`text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg mb-4`}>
          <UserCircleIcon className="h-12 w-12 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold">Loading profile...</h2>
      </div>
    </div>
  );
};